/**
 * Zustand store for Stock data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const STOCK_KEY = "khays_stock";

export interface StockData { [key: string]: number; }

function loadFromStorage(): StockData {
  try { const s = localStorage.getItem(STOCK_KEY); if (s) return JSON.parse(s); } catch {} return {};
}

interface StockState {
  stock: StockData;
  _rev: number;
  setStock: (stock: StockData) => void;
  reload: () => void;
}

export const useStockStore = create<StockState>()(
  devtools(
    (set, get) => ({
      stock: loadFromStorage(),
      _rev: 0,
      setStock: (stock) => {
        localStorage.setItem(STOCK_KEY, JSON.stringify(stock));
        set({ stock, _rev: get()._rev + 1 });
      },
      reload: () => set({ stock: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "StockStore" }
  )
);

// ─── Backward-compatible exports ──────────────────────────────

export function loadStock(): StockData { return useStockStore.getState().stock; }

function saveStock(stock: StockData): void { useStockStore.getState().setStock(stock); }

export function getStockQuantity(key: string): number | undefined { return loadStock()[key]; }

export function setStockQuantity(key: string, quantity: number, reason?: string): void {
  const stock = { ...loadStock() };
  if (quantity < 0) quantity = 0;
  const prev = stock[key];
  stock[key] = quantity;
  saveStock(stock);
  // Fire-and-forget audit log (dynamic import avoids circular deps)
  try {
    import("@/data/stockAuditStore").then(m => m.logStockChange({ sku: key, previous: prev, next: quantity, reason: reason || "manual" }));
  } catch {}
}

export function incrementStock(key: string, amount: number = 1): void {
  const stock = { ...loadStock() };
  stock[key] = (stock[key] ?? 0) + amount;
  saveStock(stock);
}

export function decrementStock(key: string, amount: number = 1): void {
  const stock = { ...loadStock() };
  if (!(key in stock)) return;
  stock[key] = Math.max(0, stock[key] - amount);
  saveStock(stock);
}

export function isInStock(key: string): boolean {
  const qty = getStockQuantity(key);
  return qty === undefined ? true : qty > 0;
}

export function hasStockTracking(key: string): boolean { return getStockQuantity(key) !== undefined; }

export function removeStockTracking(key: string): void {
  const stock = { ...loadStock() };
  const prev = stock[key];
  delete stock[key];
  saveStock(stock);
  try {
    import("@/data/stockAuditStore").then(m => m.logStockChange({ sku: key, previous: prev, next: undefined, reason: "untrack" }));
  } catch {}
}

export function getLowStockThreshold(deviceName: string): number {
  // Import inline to avoid circular dependency
  const { loadAdminSettings } = require("@/stores/adminStore");
  const settings = loadAdminSettings();
  return settings.lowStockThresholds[deviceName] ?? 3;
}

export function getStockStatus(key: string): "in_stock" | "low_stock" | "out_of_stock" | "untracked" {
  const qty = getStockQuantity(key);
  if (qty === undefined) return "untracked";
  if (qty === 0) return "out_of_stock";
  const deviceName = key.split("|")[0];
  const threshold = getLowStockThreshold(deviceName);
  if (qty <= threshold) return "low_stock";
  return "in_stock";
}

export function getLowStockItems(): { key: string; quantity: number; deviceName: string; threshold: number }[] {
  const stock = loadStock();
  const items: { key: string; quantity: number; deviceName: string; threshold: number }[] = [];
  for (const [key, qty] of Object.entries(stock)) {
    const deviceName = key.split("|")[0];
    const threshold = getLowStockThreshold(deviceName);
    if (qty <= threshold) items.push({ key, quantity: qty, deviceName, threshold });
  }
  return items.sort((a, b) => a.quantity - b.quantity);
}
